// frontend/src/components/dashboard/leadership/ScoreRadialPerformance.jsx
// ✅ Score TOTAL sempre (não depende de período)
// ✅ deltaPts é só exibido como "movimentação", NÃO soma no scoreTotal
// ✅ Versão 6K (6 pilares de 1000 pts cada)
// ✅ Movimentação do mês alinhada com (Comissão + Pontos ELION)
// ✅ Regras no rodapé ocupando largura total

import React, { useMemo } from 'react';
import {
  Trophy,
  TrendingUp,
  Briefcase,
  CalendarCheck,
  AlertTriangle,
  Home,
  SignpostBig,
  BadgeDollarSign,
  Sparkles,
} from 'lucide-react';

/* =======================
   PALETA — AURORA (HERO)
======================= */
const AURORA = {
  emerald: '#34D399',
  purple: '#A78BFA',
  amber: '#F59E0B',
  border: 'rgba(255,255,255,0.10)',
  text: 'rgba(255,255,255,0.92)',
  text2: 'rgba(255,255,255,0.65)',
  text3: 'rgba(255,255,255,0.45)',
  cyan: '#22D3EE',
  rose: '#FB7185',
  lime: '#A3E635',
};

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const round = (n) => Math.round(Number(n || 0));

function formatBRLShort(v) {
  const n = Number(v || 0);
  if (n >= 1000000)
    return `R$ ${(n / 1000000).toFixed(1).replace('.', ',')} mi`;
  if (n >= 1000) return `R$ ${(n / 1000).toFixed(0)} mil`;
  return `R$ ${n.toFixed(0)}`;
}

function AccentLine({ variant = 'mix' }) {
  const bg =
    variant === 'emerald'
      ? `linear-gradient(90deg, ${AURORA.emerald}, rgba(52,211,153,0.15))`
      : variant === 'purple'
        ? `linear-gradient(90deg, ${AURORA.purple}, rgba(167,139,250,0.15))`
        : variant === 'amber'
          ? `linear-gradient(90deg, ${AURORA.amber}, rgba(245,158,11,0.14))`
          : `linear-gradient(90deg, ${AURORA.emerald}, ${AURORA.purple})`;

  return (
    <div
      className="pointer-events-none absolute inset-x-0 top-0 h-[2px]"
      style={{ background: bg }}
    />
  );
}

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function describeArc(cx, cy, r, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
}

function RingArc({
  cx,
  cy,
  r,
  strokeWidth,
  pct,
  color,
  trackColor = 'rgba(255,255,255,0.08)',
}) {
  const p = clamp(Number(pct || 0), 0, 100);
  const start = 0;
  const end = (p / 100) * 360;

  const trackPath = describeArc(cx, cy, r, 0, 359.999);
  const arcPath = describeArc(cx, cy, r, start, Math.max(0.001, end));

  return (
    <>
      <path
        d={trackPath}
        fill="none"
        stroke={trackColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <path
        d={arcPath}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        style={{ filter: 'drop-shadow(0px 0px 10px rgba(255,255,255,0.05))' }}
      />
    </>
  );
}

function BreakdownRow({ Icon, label, value, max, color, pct }) {
  return (
    <div
      className="flex items-center justify-between rounded-2xl border px-3 py-2"
      style={{
        borderColor: AURORA.border,
        background: 'rgba(255,255,255,0.05)',
      }}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span
          className="grid h-9 w-9 place-content-center rounded-2xl border"
          style={{
            borderColor: AURORA.border,
            background: 'rgba(255,255,255,0.06)',
          }}
        >
          {Icon ? <Icon size={16} style={{ color: AURORA.text2 }} /> : null}
        </span>

        <div className="min-w-0 leading-tight">
          <div
            className="text-[11px] font-semibold uppercase tracking-[0.14em] truncate"
            style={{ color: AURORA.text3 }}
          >
            {label}
          </div>

          <div className="mt-1 flex items-center gap-2">
            <div
              className="text-sm font-extrabold"
              style={{ color: AURORA.text }}
            >
              {round(pct)}%
            </div>
            <div className="text-[11px]" style={{ color: AURORA.text2 }}>
              ({round(value)} / {round(max)})
            </div>
          </div>

          <div
            className="mt-2 h-1.5 w-full overflow-hidden rounded-full"
            style={{ background: 'rgba(255,255,255,0.08)' }}
          >
            <div
              className="h-full rounded-full"
              style={{
                width: `${clamp(pct, 0, 100)}%`,
                background: `linear-gradient(90deg, ${color}, rgba(255,255,255,0.10))`,
              }}
            />
          </div>
        </div>
      </div>

      <span
        className="ml-3 h-2.5 w-2.5 shrink-0 rounded-full"
        style={{
          backgroundColor: color,
          boxShadow: '0 0 0 6px rgba(255,255,255,0.04)',
        }}
        aria-hidden
      />
    </div>
  );
}

function MiniStat({ title, value }) {
  return (
    <div
      className="rounded-2xl border px-3 py-2"
      style={{
        borderColor: AURORA.border,
        background: 'rgba(255,255,255,0.05)',
      }}
    >
      <div
        className="text-[10px] font-semibold uppercase tracking-[0.14em]"
        style={{ color: AURORA.text3 }}
      >
        {title}
      </div>
      <div className="text-sm font-extrabold" style={{ color: AURORA.text }}>
        {value}
      </div>
    </div>
  );
}

function MovimentacaoCompacta({ delta, mov }) {
  const abs = Math.abs(Number(delta || 0));
  const isZero = abs === 0;

  const badgeTone =
    delta > 0
      ? {
          border: 'rgba(52,211,153,0.25)',
          bg: 'rgba(52,211,153,0.08)',
          text: AURORA.emerald,
        }
      : delta < 0
        ? {
            border: 'rgba(245,158,11,0.22)',
            bg: 'rgba(245,158,11,0.08)',
            text: AURORA.amber,
          }
        : {
            border: 'rgba(255,255,255,0.12)',
            bg: 'rgba(255,255,255,0.05)',
            text: AURORA.text2,
          };

  const Icon = mov?.Icon || AlertTriangle;
  const status = delta > 0 ? 'Impulso' : delta < 0 ? 'Penitência' : 'Neutro';

  return (
    <div
      className="h-full min-h-[122px] rounded-3xl border p-5"
      style={{
        borderColor: AURORA.border,
        background: 'rgba(255,255,255,0.05)',
      }}
    >
      <div className="flex h-full flex-col justify-between gap-3">
        <div className="flex items-center justify-between">
          <div
            className="text-[10px] font-black uppercase tracking-[0.22em]"
            style={{ color: AURORA.text2 }}
          >
            Movimentação do mês
          </div>
          <div
            className="inline-flex items-center gap-1 rounded-xl border px-2 py-1 text-[10px] font-extrabold uppercase tracking-[0.14em]"
            style={{
              borderColor: badgeTone.border,
              background: badgeTone.bg,
              color: badgeTone.text,
            }}
          >
            <Icon size={12} />
            {status}
          </div>
        </div>

        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <div
              className="truncate text-[12px] font-semibold"
              style={{ color: AURORA.text }}
            >
              {mov?.headline}
            </div>
            <div
              className="mt-1 text-[11px] leading-tight"
              style={{ color: AURORA.text3 }}
            >
              {delta < 0
                ? 'Sem ações registradas no período.'
                : 'Ações registradas no período.'}
            </div>
          </div>

          <div className="shrink-0 text-right">
            <div
              className="text-[22px] font-extrabold leading-none"
              style={{ color: AURORA.text }}
            >
              {isZero
                ? '0'
                : delta > 0
                  ? `+${round(delta)}`
                  : `${round(delta)}`}
              <span className="ml-1 text-[10px] font-semibold opacity-50">
                pts
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ScoreRadialPerformance({
  loading = false,
  totalMax = 6000,
  scoreTotal = 0,
  deltaPts = 0,
  renda12m = 0,
  nivelLabel = 'Iniciante',
  nivelFaixa = 'até R$ 59 mil',
  imoveisPts = 0,
  negociosPts = 0,
  placasPts = 0,
  visitasPts = 0,
  comissaoPts = 0,
  elionPts = 0,
  imoveisMax = 1000,
  negociosMax = 1000,
  placasMax = 1000,
  visitasMax = 1000,
  comissaoMax = 1000,
  elionMax = 1000,
  regraPenitenciaLabel = 'Sem movimento no mês',
  regraPenitenciaPts = '-100 pts',
  regraBonusLabel = 'Full-player no mês',
  regraBonusPts = '+85 pts',
  regraJanelaLabel = 'Janela móvel',
  regraJanelaValue = '12 meses',
}) {
  const delta = Number(deltaPts || 0);
  const isImpulso = delta > 0;

  const items = useMemo(() => {
    const pctOf = (pts, max) =>
      max ? (Number(pts || 0) / Number(max || 1)) * 100 : 0;
    return [
      {
        key: 'imoveis',
        label: 'Imóveis',
        value: imoveisPts,
        max: imoveisMax,
        pct: pctOf(imoveisPts, imoveisMax),
        color: AURORA.emerald,
        Icon: Home,
      },
      {
        key: 'negocios',
        label: 'Negócios',
        value: negociosPts,
        max: negociosMax,
        pct: pctOf(negociosPts, negociosMax),
        color: AURORA.cyan,
        Icon: Briefcase,
      },
      {
        key: 'placas',
        label: 'Placas',
        value: placasPts,
        max: placasMax,
        pct: pctOf(placasPts, placasMax),
        color: AURORA.lime,
        Icon: SignpostBig,
      },
      {
        key: 'visitas',
        label: 'Visitas',
        value: visitasPts,
        max: visitasMax,
        pct: pctOf(visitasPts, visitasMax),
        color: AURORA.amber,
        Icon: CalendarCheck,
      },
      {
        key: 'comissao',
        label: 'Comissão',
        value: comissaoPts,
        max: comissaoMax,
        pct: pctOf(comissaoPts, comissaoMax),
        color: AURORA.purple,
        Icon: BadgeDollarSign,
      },
      {
        key: 'elion',
        label: 'Pontos ELION',
        value: elionPts,
        max: elionMax,
        pct: pctOf(elionPts, elionMax),
        color: AURORA.rose,
        Icon: Sparkles,
      },
    ];
  }, [
    imoveisPts,
    negociosPts,
    placasPts,
    visitasPts,
    comissaoPts,
    elionPts,
    imoveisMax,
    negociosMax,
    placasMax,
    visitasMax,
    comissaoMax,
    elionMax,
  ]);

  const top4 = items.slice(0, 4);
  const bottom2 = items.slice(4, 6);

  const scoreBase = useMemo(
    () => clamp(Number(scoreTotal || 0), 0, Number(totalMax || 6000)),
    [scoreTotal, totalMax]
  );
  const progressTotal = useMemo(
    () => (totalMax ? (scoreBase / totalMax) * 100 : 0),
    [scoreBase, totalMax]
  );

  const mov = useMemo(() => {
    const abs = Math.abs(delta);
    if (isImpulso) {
      let headline = 'Boa! Você ganhou tração no mês.';
      if (abs >= 50) headline = 'Excelente! Ritmo forte.';
      return { tag: 'Impulso', headline, Icon: TrendingUp };
    }
    return {
      tag: 'Penitência',
      headline: abs > 0 ? 'Vamos virar o jogo.' : 'Sem movimentação.',
      Icon: AlertTriangle,
    };
  }, [delta, isImpulso]);

  if (loading)
    return <div className="animate-pulse h-[600px] bg-white/5 rounded-3xl" />;

  const size = 260;
  const cx = size / 2;
  const cy = size / 2;

  const rings = [
    { r: 114, w: 9, item: items[5] },
    { r: 100, w: 9, item: items[4] },
    { r: 86, w: 9, item: items[3] },
    { r: 72, w: 9, item: items[2] },
    { r: 58, w: 9, item: items[1] },
    { r: 44, w: 9, item: items[0] },
  ];

  return (
    <section
      className="relative overflow-hidden rounded-3xl border bg-zinc-950/80 p-6 shadow-2xl backdrop-blur"
      style={{ borderColor: AURORA.border }}
    >
      <AccentLine variant="mix" />

      {/* Header */}
      <div className="relative z-10 mb-6">
        <div
          className="inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-[11px] font-black uppercase tracking-[0.22em]"
          style={{
            borderColor: AURORA.border,
            background: 'rgba(255,255,255,0.05)',
            color: AURORA.text2,
          }}
        >
          <Trophy size={14} />
          Score profissional (6.000)
        </div>
      </div>

      {/* ===== LINHA 1 (Topo): Gráfico + TOP4 pilares ===== */}
      <div className="relative z-10 grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr] lg:items-start">
        {/* Coluna esquerda */}
        <div className="flex flex-col">
          <div className="relative grid place-items-center">
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
              {rings.map((r, i) => (
                <RingArc
                  key={i}
                  cx={cx}
                  cy={cy}
                  r={r.r}
                  strokeWidth={r.w}
                  pct={r.item.pct}
                  color={r.item.color}
                />
              ))}
              <circle
                cx={cx}
                cy={cy}
                r={32}
                fill="rgba(255,255,255,0.04)"
                stroke="rgba(255,255,255,0.08)"
              />
            </svg>

            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
              <div
                className="text-4xl font-extrabold leading-none"
                style={{ color: AURORA.text }}
              >
                {round(scoreBase)}
                <span className="text-base font-semibold opacity-40">
                  /{round(totalMax)}
                </span>
              </div>
              <div className="mt-1 text-xs" style={{ color: AURORA.text2 }}>
                Progresso: {progressTotal.toFixed(0)}%
              </div>
              <div
                className="mt-2 text-[11px] font-bold"
                style={{ color: AURORA.text }}
              >
                {nivelLabel}
              </div>
              <div className="text-[10px]" style={{ color: AURORA.text3 }}>
                Renda 12m: {formatBRLShort(renda12m)}
              </div>
            </div>
          </div>

          {/* Barra de Progresso Elite */}
          <div className="mt-4">
            <div className="mb-2 flex items-center justify-between text-[10px] uppercase font-bold tracking-widest opacity-40">
              <span>Iniciante</span>
              <span>Elite</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/5">
              <div
                className="h-full rounded-full transition-all duration-1000"
                style={{
                  width: `${progressTotal}%`,
                  background: `linear-gradient(90deg, ${AURORA.emerald}, ${AURORA.purple})`,
                }}
              />
            </div>
          </div>
        </div>

        {/* Coluna direita: TOP4 */}
        <div className="flex flex-col gap-2">
          {top4.map((it) => (
            <BreakdownRow
              key={it.key}
              Icon={it.Icon}
              label={it.label}
              value={it.value}
              max={it.max}
              pct={it.pct}
              color={it.color}
            />
          ))}
        </div>
      </div>

      {/* ===== LINHA 2 (Inferior): Movimentação alinhada com (Comissão + Pontos ELION) ===== */}
      <div className="relative z-10 mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1fr]">
        {/* Esquerda: Movimentação (altura cheia) */}
        <MovimentacaoCompacta delta={delta} mov={mov} />

        {/* Direita: BOTTOM2 empilhados (comissão + elion) */}
        <div className="flex h-full flex-col gap-2">
          {bottom2.map((it) => (
            <BreakdownRow
              key={it.key}
              Icon={it.Icon}
              label={it.label}
              value={it.value}
              max={it.max}
              pct={it.pct}
              color={it.color}
            />
          ))}
        </div>
      </div>

      {/* ===== RODAPÉ: Regras ===== */}
      <div
        className="relative z-10 mt-4 rounded-3xl border p-4"
        style={{
          borderColor: AURORA.border,
          background: 'rgba(255,255,255,0.03)',
        }}
      >
        <div className="mb-3 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-white/40">
          <AlertTriangle size={14} /> Regras do ELION (Mês)
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <MiniStat title={regraPenitenciaLabel} value={regraPenitenciaPts} />
          <MiniStat title={regraBonusLabel} value={regraBonusPts} />
          <MiniStat title={regraJanelaLabel} value={regraJanelaValue} />
        </div>
      </div>
    </section>
  );
}
